import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://tcio-api.onrender.com';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('tcio_token')}` } });

const C = {
  canvas: '#F4F8FB', surface: '#FFFFFF', ink: '#22303E', sec: '#5D6B7A',
  deep: '#336090', act: '#0878E8', border: '#D8E3EE',
  green: '#16A36A', greenBg: '#EAF8F1', amber: '#E99A18', amberBg: '#FFF6E5',
  red: '#E5484D', redBg: '#FDECEE', purple: '#7C5CE7', purpleBg: '#F1EEFF',
  teal: '#18A6B8', tealBg: '#E8F7F9', blue: '#4479B0', blueBg: '#EAF1F8',
};
const LOC_STYLE = {
  DTX: { bg: '#FDF6E3', color: '#B7791F' }, RTC: { bg: C.blueBg, color: C.blue },
  PHP: { bg: C.greenBg, color: C.green }, IOP: { bg: C.purpleBg, color: C.purple },
};
const fmt$ = (n) => (n === null || n === undefined) ? '--' : '$' + Math.round(n).toLocaleString();
const fmtD = (v) => v ? new Date(v + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) : '--';

function mondayOf(offsetWeeks) {
  const nowPT = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const back = (nowPT.getDay() + 6) % 7;
  const mon = new Date(nowPT.getFullYear(), nowPT.getMonth(), nowPT.getDate() - back - offsetWeeks * 7);
  return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, '0')}-${String(mon.getDate()).padStart(2, '0')}`;
}

export default function BillingCensus() {
  const [facilities, setFacilities] = useState([]);
  const [facility, setFacility] = useState('');
  const [weekStart, setWeekStart] = useState(mondayOf(1)); // default: prior week (what gets sent)
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState(null); // episode_id of the expanded row
  const [grids, setGrids] = useState({}); // episode_id -> day-grid payload (cached)
  const [sending, setSending] = useState(false);

  const role = (() => { try { return JSON.parse(atob(localStorage.getItem('tcio_token').split('.')[1])).role; } catch { return null; } })();
  const canSend = ['ceo', 'admin'].includes(role);

  useEffect(() => {
    fetch(`${API}/api/facilities`, AUTH()).then(r => r.json()).then(d => {
      const all = d.ok ? d.data : [];
      // Real orgs: hide sandbox from the census dropdown (Monday-send safety).
      // Sandbox-only viewers (demo logins): the filter would empty the list - show their sandboxes.
      const nonSbx = all.filter(f => !f.is_sandbox);
      const facs = nonSbx.length ? nonSbx : all;
      setFacilities(facs);
      if (facs.length && !facility) setFacility(facs[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!facility) return;
    setLoading(true); setErr('');
    try {
      const [cR, qR] = await Promise.all([
        fetch(`${API}/api/billing-census?facilityId=${facility}&weekStart=${weekStart}`, AUTH()),
        fetch(`${API}/api/billing-census/flip-queue?facilityId=${facility}`, AUTH()),
      ]);
      const cD = await cR.json();
      const qD = await qR.json();
      if (cD.ok) setData(cD); else { setData(null); setErr(cD.error || 'Load failed'); }
      setQueue(qD.ok ? qD.data : []);
    } catch (e) { setErr('Load failed'); }
    setLoading(false);
  }, [facility, weekStart]);
  useEffect(() => { load(); }, [load]);

  const toggleRow = async (episodeId) => {
    if (expanded === episodeId) { setExpanded(null); return; }
    setExpanded(episodeId);
    if (!grids[episodeId]) {
      try {
        const r = await fetch(`${API}/api/ur/day-grid/${episodeId}`, AUTH());
        const d = await r.json();
        if (d.ok) setGrids(g => ({ ...g, [episodeId]: d }));
      } catch (e) { /* silent */ }
    }
  };

  const sendWeek = async () => {
    if (!data || !data.week) return;
    if (!window.confirm(`Mark week of ${fmtD(data.week.week_start)} as SENT to Biller?\n\nThis freezes the snapshot and emails the census to billing (cc you, Catherine, Tina). Only the authorization sweep can update it afterward.`)) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/billing-census/${data.week.id}/send`, { method: 'POST', ...AUTH() });
      const d = await r.json();
      if (!d.ok) alert(d.error || 'Send failed'); else load();
    } catch (e) { alert('Send failed'); }
    setSending(false);
  };

  const removeItem = async (id) => {
    try {
      await fetch(`${API}/api/billing-census/flip-queue/${id}/remove`, { method: 'POST', ...AUTH() });
      load();
    } catch (e) { /* silent */ }
  };

  const markWorked = async (id) => {
    try {
      await fetch(`${API}/api/billing-census/flip-queue/${id}/worked`, { method: 'POST', ...AUTH() });
      load();
    } catch (e) { /* silent */ }
  };

  const weekDays = (() => {
    if (!data) return [];
    const start = new Date(data.week.week_start + 'T00:00:00Z');
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start.getTime() + i * 86400000);
      return { iso: d.toISOString().slice(0, 10), label: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i] };
    });
  })();

  const cellChip = (dv) => {
    if (!dv) return null;
    if (dv.state === 'dc') return <span style={{ background: '#22303E', color: '#fff', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>DC</span>;
    if (dv.state === 'off') return <span style={{ color: C.sec, fontSize: 11.5, fontWeight: 700 }}>O</span>;
    if (dv.state === 'pending') return <span style={{ background: C.redBg, color: C.red, borderRadius: 5, padding: '2px 7px', fontSize: 11, fontWeight: 800 }}>Pending</span>;
    if (dv.state === 'denied') return <span style={{ background: '#F1EEFF', color: '#7C5CE7', borderRadius: 5, padding: '2px 7px', fontSize: 11, fontWeight: 800 }} title={(dv.detail || '') + ' - auto-queued for retro billing'}>Denied</span>;
    if (dv.state === 'exception') return <span style={{ background: C.amberBg, color: C.amber, borderRadius: 5, padding: '2px 7px', fontSize: 11, fontWeight: 800 }} title={dv.detail || ''}>Exc</span>;
    const s = LOC_STYLE[dv.loc] || { bg: C.blueBg, color: C.blue };
    const flipped = !!dv.flipped_at;
    return (
      <span style={{ background: s.bg, color: s.color, borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 800, border: flipped ? `1.5px solid ${C.green}` : 'none' }}
        title={(dv.auth_number ? 'Auth #' + dv.auth_number : '') + (flipped ? ' - authorized after census via sweep' : '')}>
        {dv.loc}
      </span>
    );
  };

  const sent = data && data.week.status === 'sent';

  return (
    <div style={{ background: C.canvas, minHeight: '100vh', color: C.ink, fontFamily: "'Inter','Helvetica Neue',Arial,sans-serif", padding: '20px 24px' }}>
      <div style={{ maxWidth: 1500, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Weekly Billing Census</div>
            <div style={{ fontSize: 13, color: C.sec, marginTop: 2 }}>Per-facility Monday-Sunday census. Live until sent; the authorization sweep keeps it honest afterward.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={facility} onChange={e => setFacility(e.target.value)}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, padding: '8px 12px', minWidth: 180 }}>
              {facilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
            </select>
            <select value={weekStart} onChange={e => setWeekStart(e.target.value)}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, padding: '8px 12px' }}>
              {Array.from({ length: 12 }, (_, i) => mondayOf(i)).map(m => (
                <option key={m} value={m}>Week of {fmtD(m)}{m === mondayOf(1) ? ' (prior week)' : m === mondayOf(0) ? ' (current)' : ''}</option>
              ))}
            </select>
            <button onClick={load} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.deep, fontSize: 13, padding: '8px 14px', cursor: 'pointer' }}>Refresh</button>
            {data && (
              <button onClick={async () => {
                try {
                  const r = await fetch(`${API}/api/billing-census/${data.week.id}/export.csv`, AUTH());
                  const blob = await r.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `BillingCensus_${weekStart}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { alert('Download failed'); }
              }}
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.deep, fontSize: 13, padding: '8px 14px', cursor: 'pointer' }}>
                Download CSV
              </button>
            )}
            {canSend && data && !sent && (
              <button onClick={sendWeek} disabled={sending}
                style={{ background: C.act, border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, padding: '8px 16px', cursor: 'pointer' }}>
                {sending ? 'Sending...' : 'Send to Biller'}
              </button>
            )}
          </div>
        </div>

        {data && (
          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, padding: '4px 12px', borderRadius: 6, background: sent ? C.greenBg : C.amberBg, color: sent ? C.green : C.amber }}>
              {sent ? `SENT ${new Date(data.week.sent_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : 'OPEN - regenerates live'}
            </span>
            <span style={{ fontSize: 12.5, color: C.sec }}>{data.patients.length} patients · {fmtD(data.week.week_start)} to {fmtD(weekDays[6] ? weekDays[6].iso : null)}</span>
          </div>
        )}

        {/* MONEY STRIP */}
        {data && data.totals && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginTop: 14 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.sec }}>Billable Days</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{data.totals.billable_days}</div>
            </div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.sec }}>Billed Value</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: C.deep }}>{fmt$(data.totals.billed_value)}</div>
              <div style={{ fontSize: 11, color: C.sec }}>at facility billed rates</div>
            </div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.sec }}>Pending Days</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: data.totals.pending_days > 0 ? C.red : C.ink }}>{data.totals.pending_days}</div>
            </div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.sec }}>On Hold (Pending)</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: C.red }}>{fmt$(data.totals.pending_hold_value)}</div>
              <div style={{ fontSize: 11, color: C.sec }}>awaiting authorization</div>
            </div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.sec }}>Realistic Value</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: C.green }}>{fmt$(data.totals.realistic_value)}</div>
              <div style={{ fontSize: 11, color: C.sec }}>payer history, billable days</div>
            </div>
          </div>
        )}

        {/* NEWLY AUTHORIZED QUEUE */}
        {queue.length > 0 && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.green}`, borderRadius: 10, padding: '14px 16px', marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.green }}>Bill Now - New Auths &amp; Retro Candidates ({queue.length})</div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {queue.map(q => (
                <div key={q.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 5, background: q.kind === 'retro' ? '#F1EEFF' : C.greenBg, color: q.kind === 'retro' ? '#7C5CE7' : C.green }}>
                    {q.kind === 'retro' ? 'RETRO' : 'NEW AUTH'}
                  </span>
                  <b>{q.patient_name}</b>
                  <span>{q.days_count} {q.loc} day(s) {fmtD(String(q.date_from).slice(0, 10))}-{fmtD(String(q.date_to).slice(0, 10))}</span>
                  <span style={{ color: C.sec }}>auth #{q.auth_number || 'n/a'} · week of {fmtD(String(q.week_start).slice(0, 10))}</span>
                  <span style={{ flex: 1 }} />
                  {q.kind === 'retro' && canSend && (
                    <button onClick={() => { if (window.confirm('Remove from retro billing? Use when a denial is final (lost appeal) and should NOT be billed.')) removeItem(q.id); }}
                      style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.red, fontSize: 12, padding: '3px 10px', cursor: 'pointer' }}>
                      Don't Bill
                    </button>
                  )}
                  <button onClick={() => markWorked(q.id)}
                    style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.deep, fontSize: 12, padding: '3px 10px', cursor: 'pointer' }}>
                    Mark Billed
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {err && <div style={{ background: C.redBg, color: C.red, borderRadius: 8, padding: 10, marginTop: 14, fontWeight: 700, fontSize: 13 }}>{err}</div>}
        {loading && <div style={{ color: C.sec, padding: 30, textAlign: 'center' }}>Loading census...</div>}

        {/* THE GRID */}
        {data && !loading && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 14, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 1100 }}>
              <thead>
                <tr style={{ background: '#2E4A66', color: '#fff' }}>
                  {['Patient', 'DOB', 'Admit', 'DC', 'Payer', 'Member ID'].map(h => <th key={h} style={{ textAlign: 'left', padding: '9px 10px', fontSize: 10.5, letterSpacing: '0.06em' }}>{h.toUpperCase()}</th>)}
                  {weekDays.map(d => <th key={d.iso} style={{ textAlign: 'center', padding: '9px 6px', fontSize: 10.5 }}>{d.label.toUpperCase()}</th>)}
                  <th style={{ textAlign: 'right', padding: '9px 10px', fontSize: 10.5 }}>BILLED $</th>
                  <th style={{ textAlign: 'right', padding: '9px 10px', fontSize: 10.5 }}>HOLD $</th>
                </tr>
              </thead>
              <tbody>
                {data.patients.map(p => (
                  <React.Fragment key={p.episode_id}>
                  <tr style={{ borderTop: `1px solid ${C.border}`, background: expanded === p.episode_id ? '#F7FAFD' : 'transparent' }}>
                    <td style={{ padding: '8px 10px' }}>
                      <button onClick={() => toggleRow(p.episode_id)}
                        style={{ background: 'none', border: 'none', color: C.act, fontWeight: 600, fontSize: 12.5, cursor: 'pointer', padding: 0, textAlign: 'left' }}>
                        {expanded === p.episode_id ? '▾ ' : '▸ '}{p.patient_name}
                      </button>
                    </td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtD(p.dob)}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtD(p.admit_date)}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: p.discharge_date ? C.red : C.sec }}>{p.discharge_date ? fmtD(p.discharge_date) : '--'}</td>
                    <td style={{ padding: '8px 10px', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.payer || '--'}</td>
                    <td style={{ padding: '8px 10px', fontSize: 11.5, color: C.sec }}>{p.member_id || '--'}</td>
                    {weekDays.map(d => <td key={d.iso} style={{ textAlign: 'center', padding: '8px 4px' }}>{cellChip(p.days[d.iso])}</td>)}
                    <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700 }}>{fmt$(p.money.billed_value)}</td>
                    <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, color: p.money.pending_hold_value ? C.red : C.sec }}>{fmt$(p.money.pending_hold_value)}</td>
                  </tr>
                  {expanded === p.episode_id && (
                    <tr style={{ background: '#F7FAFD' }}>
                      <td colSpan={15} style={{ padding: '4px 24px 14px' }}>
                        {!grids[p.episode_id] ? (
                          <div style={{ fontSize: 12.5, color: C.sec, padding: 8 }}>Loading authorization timeline...</div>
                        ) : (
                          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <div>
                              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.sec, marginBottom: 6 }}>Authorization Timeline</div>
                              {grids[p.episode_id].auths.length ? (
                                <table style={{ borderCollapse: 'collapse', fontSize: 12.5 }}>
                                  <thead><tr style={{ textAlign: 'left', color: C.sec, fontSize: 10.5, textTransform: 'uppercase' }}>
                                    <th style={{ padding: '3px 18px 3px 0' }}>LOC</th><th style={{ padding: '3px 18px 3px 0' }}>From</th><th style={{ padding: '3px 18px 3px 0' }}>To</th><th style={{ padding: '3px 0' }}>Auth #</th>
                                  </tr></thead>
                                  <tbody>
                                    {grids[p.episode_id].auths.map((a, i) => (
                                      <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                                        <td style={{ padding: '5px 18px 5px 0' }}>{(() => { const s = LOC_STYLE[a.loc] || {}; return <span style={{ background: s.bg, color: s.color, borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>{a.loc}</span>; })()}</td>
                                        <td style={{ padding: '5px 18px 5px 0' }}>{fmtD(a.date_from)}</td>
                                        <td style={{ padding: '5px 18px 5px 0' }}>{fmtD(a.date_to)}</td>
                                        <td style={{ padding: '5px 0', fontWeight: 600 }}>{a.auth_number || '--'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : <div style={{ fontSize: 12.5, color: C.red, fontWeight: 700 }}>No authorizations on file - all days pending</div>}
                            </div>
                            <div style={{ fontSize: 12.5, color: C.sec }}>
                              Admitted {fmtD(grids[p.episode_id].admit_date)}{grids[p.episode_id].discharge_date ? ` · DC ${fmtD(grids[p.episode_id].discharge_date)}` : ''}
                              {grids[p.episode_id].computed_unauth > 0 && (
                                <div style={{ color: C.amber, fontWeight: 700, marginTop: 4 }}>{grids[p.episode_id].computed_unauth} uncovered day(s) across the full episode</div>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ fontSize: 11.5, color: C.sec, marginTop: 10 }}>
          O = LOC does not bill that day · Pending = in treatment, no auth covering the day · Denied = auth denied, auto-queued for retro billing · Exc = ruled not billable in UR (scholarship, DC day) - reversible there · green-ring chip = authorized after census via sweep · click a patient for their full auth timeline
        </div>
      </div>

    </div>
  );
}
